import styled from "styled-components";
import { theme } from "../theme";

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  /* Anchors the "new messages" pill, which floats above the composer. Without
     this it would position against some distant ancestor — the same trap the
     emoji picker fell into. */
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 100%;
    height: calc(var(--vh, 1vh) * 100); /* iOS Safari fix */
  }
`;

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.background.primary};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    min-height: 60px;
    position: sticky;
    top: 0;
    z-index: 10;
  }
`;

export const ChatHeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

export const ChatHeaderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.light};
  font-weight: 600;
  font-size: 1rem;
`;

export const ChatHeaderDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ChatHeaderName = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

export const ChatHeaderStatus = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
`;

export const ChatHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

export const ChatActionButton = styled.button`
  padding: ${theme.spacing.sm};
  border: none;
  background: transparent;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
  }
`;

export const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.background.secondary};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.text.secondary};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    -webkit-overflow-scrolling: touch; /* iOS smooth scrolling */
    
    /* Hide scrollbar on mobile */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

export const MessageItem = styled.div<{ $isOwn: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.$isOwn ? "flex-end" : "flex-start")};
  margin-bottom: ${theme.spacing.sm};
`;

export const MessageBubble = styled.div<{ $isOwn: boolean }>`
  max-width: 70%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  background: ${(props) =>
    props.$isOwn
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : theme.colors.background.secondary};
  color: ${(props) =>
    props.$isOwn ? theme.colors.text.light : theme.colors.text.primary};
  box-shadow: ${theme.shadows.light};
  position: relative;
  
  &:hover {
    box-shadow: ${theme.shadows.medium};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 85%;
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.xl};
    
    /* Touch-friendly message bubbles */
    &:active {
      transform: scale(0.98);
      transition: transform 0.1s ease;
    }
  }
`;

export const MessageContent = styled.div`
  word-wrap: break-word;
  line-height: 1.4;
`;

export const MessageTime = styled.div<{ $isOwn: boolean }>`
  font-size: 0.625rem;
  color: ${(props) =>
    props.$isOwn 
      ? "rgba(255, 255, 255, 0.7)" 
      : theme.colors.text.secondary};
  margin-top: ${theme.spacing.xs};
  text-align: ${(props) => (props.$isOwn ? "right" : "left")};
`;

export const MessageStatus = styled.div<{ $isOwn: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isOwn ? "flex-end" : "flex-start")};
  margin-top: ${theme.spacing.xs};
  gap: ${theme.spacing.xs};
`;

export const StatusIcon = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
`;

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  font-style: italic;
`;

export const TypingDots = styled.div`
  display: flex;
  gap: 2px;
  margin-left: ${theme.spacing.xs};
  
  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${theme.colors.text.secondary};
    animation: typing 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
  
  @keyframes typing {
    0%, 80%, 100% {
      transform: scale(0);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

export const MessageActions = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: -8px;
  right: ${theme.spacing.sm};
  display: flex;
  gap: ${theme.spacing.xs};
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity ${theme.transitions.normal};
  z-index: 10;
`;

export const MessageActionButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  box-shadow: ${theme.shadows.light};
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.text.light};
  }
`;

export const ChatLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${theme.colors.text.secondary};
`;

export const ChatError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${theme.colors.error};
  text-align: center;
  padding: ${theme.spacing.md};
`;

export const EmptyChatState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${theme.colors.text.secondary};
  text-align: center;
  padding: ${theme.spacing.md};
`;

export const EmptyChatIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.5;
`;

export const EmptyChatText = styled.p`
  font-size: 1rem;
  margin: 0;
`;
